import React, { useState } from "react";
import { MRT_ColumnDef } from "material-react-table";
import { Link, Typography, Box } from "@mui/material";
import { useNavigate } from "react-router-dom"; // Import useNavigate
import CustomTable from "../../components/custom-table/CustomTable";
import CustomButtonGroup from "../../components/custom-button-group/CustomButtonGroup";
import {
  MANAGE_REDEMPTION_SEARCH_PLACEHOLDER,
  MANAGE_REDEMPTION_TABLE_HEADER,
  TIER_STATUS,
} from "../../utility/constants/locale";
import { useGetPatientRedemptionOverviewQuery } from "./GetPatientRedemptionOverviewAPISlice"; // Import the query
import { useAppSelector } from "../../store/hooks"; // Import the hook to access Redux state
import CustomSpinner from "../../components/custom-spinner/CustomSpinner";

interface Redemption {
  mrn: string;
  patientName: string;
  patientStatus: string;
  pearl: string;
  emerald: string;
  diamond: string;
  delivery: string;
  earnedPoints: number;
  patientProfileId: number;
}

const ManageRedemptions: React.FC = () => {
  const [filter, setFilter] = useState("ALL");
  const accessToken = useAppSelector(
    (state) => state.adminAuth.user?.accessToken ?? null
  );
  const { data, error, isLoading } = useGetPatientRedemptionOverviewQuery({
    accessToken,
  });

  const handleFilterChange = (
    event: React.MouseEvent<HTMLElement>,
    newFilter: string | null
  ) => {
    if (newFilter !== null) {
      setFilter(newFilter);
    }
  };

  const renderTopToolbarCustomActions = () => {
    const buttons = [
      { value: "ALL", label: "All" },
      { value: TIER_STATUS.ELIGIBLE, label: TIER_STATUS.ELIGIBLE },
    ];
    return (
      <CustomButtonGroup
        buttons={buttons}
        value={filter}
        onChange={handleFilterChange}
      />
    );
  };

  if (isLoading) return <CustomSpinner />;
  if (error)
    return (
      <div className="error">Failed to load data. Please try again later.</div>
    );

  const filteredData =
    data?.patientRedemptionOverview.filter((item: Redemption) => {
      if (filter === TIER_STATUS.ELIGIBLE) {
        return (
          item.pearl === TIER_STATUS.ELIGIBLE ||
          item.emerald === TIER_STATUS.ELIGIBLE ||
          item.diamond === TIER_STATUS.ELIGIBLE ||
          item.delivery === TIER_STATUS.ELIGIBLE
        );
      }
      return true;
    }) || [];

  const columns: MRT_ColumnDef<Redemption>[] = [
    {
      accessorKey: "mrn",
      filterFn: "contains",
      header: "MRN No.",
      size: 120,
    },
    {
      accessorKey: "patientName",
      filterFn: "contains",
      header: "Patient Name",
      Cell: ({ row }) => {
        const navigate = useNavigate();
        const handleClick = () => {
          navigate(`/admin/reward-redemption/${row.original.patientProfileId}`);
        };
        return (
          <Link
            onClick={handleClick}
            sx={{
              fontWeight: 500,
              textDecoration: "underline",
              cursor: "pointer",
            }}
          >
            {row.original.patientName}
          </Link>
        );
      },
      size: 150,
    },
    {
      accessorKey: "patientStatus",
      filterFn: "contains",
      header: "Patient Status",
      size: 150,
    },
    {
      accessorKey: "pearl",
      filterFn: "contains",
      header: "Pearl",
      Cell: ({ renderedCellValue }) => (
        <Typography
          sx={{
            color:
              renderedCellValue === TIER_STATUS.ELIGIBLE
                ? "#FF6200"
                : "inherit",
          }}
        >
          {renderedCellValue}
        </Typography>
      ),
      size: 150,
    },
    {
      accessorKey: "emerald",
      filterFn: "contains",
      header: "Emerald",
      Cell: ({ renderedCellValue }) => (
        <Typography
          sx={{
            color:
              renderedCellValue === TIER_STATUS.ELIGIBLE
                ? "#FF6200"
                : "inherit",
          }}
        >
          {renderedCellValue}
        </Typography>
      ),
      size: 150,
    },
    {
      accessorKey: "diamond",
      filterFn: "contains",
      header: "Diamond",
      Cell: ({ renderedCellValue }) => (
        <Typography
          sx={{
            color:
              renderedCellValue === TIER_STATUS.ELIGIBLE
                ? "#FF6200"
                : "inherit",
          }}
        >
          {renderedCellValue}
        </Typography>
      ),
      size: 150,
    },
    {
      accessorKey: "delivery",
      filterFn: "contains",
      header: "Delivery",
      Cell: ({ renderedCellValue }) => (
        <Typography
          sx={{
            color:
              renderedCellValue === TIER_STATUS.ELIGIBLE
                ? "#FF6200"
                : "inherit",
          }}
        >
          {renderedCellValue}
        </Typography>
      ),
      size: 150,
    },
    {
      accessorKey: "earnedPoints",
      filterFn: "contains",
      header: "Earned Points",
      size: 120,
    },
  ];

  return (
    <Box className="ManageRedemptions">
      <CustomTable
        data={filteredData}
        columns={columns}
        title={MANAGE_REDEMPTION_TABLE_HEADER}
        renderTopToolbarCustomActions={renderTopToolbarCustomActions}
        totalRecords={filteredData.length}
        globalFilterPlaceholder={MANAGE_REDEMPTION_SEARCH_PLACEHOLDER}
      />
    </Box>
  );
};

export default ManageRedemptions;
